import type { MapRef } from 'react-map-gl/maplibre'
import { MutableRefObject} from 'react'
import { actionButtonLayerId }  from './DisplayLastFocusedPlace'
import { layerIdForSlap, extractSlapIdFromLayerId, pendingOverlayLayerIdForSlap } from '../slaps/DisplaySlaps'
import { Board, PlaceId } from '../utils/Primitives'
import { lookupSlap } from '../slaps/Slaps'

function isBlockingLayer(input: string): boolean {
    const lowerCaseInput = input.toLowerCase()
    return lowerCaseInput.includes("water") || lowerCaseInput == 'river' || lowerCaseInput == 'places_locality' || lowerCaseInput == 'places_region' || lowerCaseInput == 'places_country' || lowerCaseInput == 'bridge'
}

function isOverlayLayer(input: string): boolean {
    const lowerCaseInput = input.toLowerCase()
    return lowerCaseInput.includes("objective-bounds-layer") ||
        lowerCaseInput.includes("objective-place-highlight-layer") ||
        lowerCaseInput == 'blockplace-grid' ||
        lowerCaseInput.includes('btn-lyr') ||
        lowerCaseInput.includes('inprog-lyr')
}

function isPendingOverlayLayer(input: string): boolean {
    const lowerCaseInput = input.toLowerCase()
    return lowerCaseInput.includes('pending-overlay-lyr')
}

// move the "blocking layers" to the top of the map
// these will appear over top of everything else
export function SortLayers(props: {mapRef?: MutableRefObject<MapRef | null>, boardRef: MutableRefObject<Board>,}) {
    const { mapRef, boardRef } = props
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }
    const style = map.getStyle();
    if (!style) {
        return;
    }
    const layers = style.layers;

    // sort slap layers by (slappedAt time, slapId) to match server ordering
    const renderedSlapLayerIds = layers
        .map(layer => extractSlapIdFromLayerId(layer.id))
        .filter(id => id !== null) as PlaceId[]

    const slapOrdering = renderedSlapLayerIds
        .filter(slapId => slapId !== undefined)
        .map(slapId => {
            const slap = lookupSlap({ boardRef, slapId })
            const time = slap ? (slap.time as number) : 0
            const isDraft = slap ? Boolean(slap.isDraft) : false
            return { slapId: slapId as number, time, isDraft }
        })
        .sort((a, b) => {
            if (a.isDraft !== b.isDraft) {
                return a.isDraft ? 1 : -1
            }
            if (a.time !== b.time) {
                return a.time - b.time
            }
            return a.slapId - b.slapId
        })

    slapOrdering.forEach(entry => {
        // Move slap layer (pending overlays handled later as UI elements)
        map.moveLayer(layerIdForSlap(entry.slapId))
    })

    layers.forEach(layer => {
        if (isBlockingLayer(layer.id)) {
            map.moveLayer(layer.id)
        }
    });
    layers.forEach(layer => {
        if (isOverlayLayer(layer.id) && !isPendingOverlayLayer(layer.id)) {
            map.moveLayer(layer.id)
        }
    });
    // UI elements go on top of everything (including water/blocking layers)
    const actionButtonLayer = map.getLayer(actionButtonLayerId)
    if (actionButtonLayer) {
        map.moveLayer(actionButtonLayerId)
    }

    // Move pending overlays as UI elements
    slapOrdering.forEach(entry => {
        const pendingOverlayLayerId = pendingOverlayLayerIdForSlap(entry.slapId)
        const pendingOverlayLayer = map.getLayer(pendingOverlayLayerId)
        if (pendingOverlayLayer) {
            map.moveLayer(pendingOverlayLayerId)
        }
    })

    // Move yellow place highlight as UI element - topmost so it shows over pending overlays
    const yellowHighlightLayerId = 'lfi-bounds-fill'
    const yellowHighlightLayer = map.getLayer(yellowHighlightLayerId)
    if (yellowHighlightLayer) {
        map.moveLayer(yellowHighlightLayerId)
    }
}
